import React, { useState } from 'react';
import {
  Info,
  FileText,
  Shield,
  Heart,
  Mail,
  Globe,
  Users,
  Award,
  Zap,
  ChevronRight
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { LegalModal } from '../components/LegalModal';
import { useForceLightMode } from '../hooks/useForceLightMode';

export const AboutPage: React.FC = () => {
  useForceLightMode();
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);
  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />
      <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8" />

      <div className="space-y-8">
        {/* About Section */}
        <div className="card">
          <h2 className="text-xl font-semibold text-medical-900 mb-4 flex items-center space-x-2">
            <Info className="h-5 w-5" />
            <span>About ClinLexis</span>
          </h2>
          <div className="prose prose-medical max-w-none">
            <p className="text-medical-700 leading-relaxed mb-4">
              ClinLexis is a specialised evidence navigation and summarisation platform designed for professionals
              working in medico-legal contexts. Our platform assists with the organisation, analysis, and summarisation of
              medical evidence while maintaining the highest standards of data security and compliance.
            </p>
            <p className="text-medical-700 leading-relaxed">
              ClinLexis streamlines the complex process of managing medical cases, documents, and evidence
              while ensuring full compliance with data protection requirements.
            </p>
          </div>
        </div>

        {/* Key Features */}
        <div className="card">
          <h2 className="text-xl font-semibold text-medical-900 mb-4 flex items-center space-x-2">
            <Zap className="h-5 w-5" />
            <span>Key Features</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: FileText,
                title: 'Document Management',
                description: 'Secure upload, organisation, and management of medical documents and evidence'
              },
              {
                icon: Shield,
                title: 'GDPR Compliant',
                description: 'Full compliance with UK data protection regulations'
              },
              {
                icon: Users,
                title: 'Multi-User Support',
                description: 'Collaborative platform supporting multiple professionals per case'
              },
              {
                icon: Award,
                title: 'Evidence Analysis',
                description: 'Advanced tools for analysing and summarising complex medical evidence'
              }
            ].map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-medical-50 rounded-md">
                <div className="w-8 h-8 bg-primary-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <feature.icon className="h-4 w-4 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-medium text-medical-900">{feature.title}</h3>
                  <p className="text-sm text-medical-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Support & Contact */}
        <div className="card">
          <h2 className="text-xl font-semibold text-medical-900 mb-4 flex items-center space-x-2">
            <Mail className="h-5 w-5" />
            <span>Contact</span>
          </h2>
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <Mail className="h-4 w-4 text-medical-400" />
              <span className="text-medical-600">support@clinlexis.co.uk</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Globe className="h-4 w-4 text-medical-400" />
              <span className="text-medical-600">clinlexis.co.uk</span>
            </div>
          </div>
        </div>

        {/* Legal & Compliance */}
        <div className="card">
          <h2 className="text-xl font-semibold text-medical-900 mb-4 flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>Legal & Compliance</span>
          </h2>
          <div className="space-y-4">
            <button
              onClick={() => setLegalModal('privacy')}
              className="w-full flex items-center justify-between p-3 bg-medical-50 rounded-md hover:bg-medical-100 transition-colors text-left"
            >
              <div>
                <p className="font-medium text-medical-900">Privacy Policy</p>
                <p className="text-sm text-medical-600">How we handle and protect your data</p>
              </div>
              <ChevronRight className="h-4 w-4 text-medical-400" />
            </button>
            <button
              onClick={() => setLegalModal('terms')}
              className="w-full flex items-center justify-between p-3 bg-medical-50 rounded-md hover:bg-medical-100 transition-colors text-left"
            >
              <div>
                <p className="font-medium text-medical-900">Terms of Service</p>
                <p className="text-sm text-medical-600">Terms and conditions for using ClinLexis</p>
              </div>
              <ChevronRight className="h-4 w-4 text-medical-400" />
            </button>
            <button
              onClick={() => setLegalModal('privacy')}
              className="w-full flex items-center justify-between p-3 bg-medical-50 rounded-md hover:bg-medical-100 transition-colors text-left"
            >
              <div>
                <p className="font-medium text-medical-900">GDPR Compliance</p>
                <p className="text-sm text-medical-600">Our commitment to data protection</p>
              </div>
              <ChevronRight className="h-4 w-4 text-medical-400" />
            </button>
          </div>
        </div>

      </div>

      <LegalModal
        isOpen={legalModal !== null}
        onClose={() => setLegalModal(null)}
        type={legalModal || 'terms'}
      />
      </div>
    </div>
  );
};